import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Users, Building2, Sun, Battery, Zap, Sparkles, 
  AlertTriangle, ArrowUpRight, FileText, Inbox, Clock, MapPin, CheckCircle2, X, Calendar as CalendarIcon, Camera
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { dashboardAPI, cleanerAPI, scheduledInstallationAPI } from '../lib/api';
import { 
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer
} from 'recharts';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { formatDate, getGoogleMapsUrl } from '../lib/utils';
import InstallationMap from './InstallationMap';
import NotificationsPanel from '../components/NotificationsPanel';
import MyTicketsPanel from '../components/MyTicketsPanel';
import CleaningPhotosPanel from '../components/CleaningPhotosPanel';

const StatCard = ({ icon: Icon, label, value, subValue, color, href }) => (
  <Link to={href || '#'}>
    <Card className="stat-card cursor-pointer group" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className={`w-10 h-10 rounded-md flex items-center justify-center bg-${color}-500/20`}>
            <Icon className={`w-5 h-5 text-${color}-400`} strokeWidth={1.5} />
          </div>
          <ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <div className="mt-4">
          <p className="text-3xl font-bold tracking-tight">{value}</p>
          <p className="text-sm text-muted-foreground mt-1">{label}</p>
          {subValue && <p className="text-xs text-muted-foreground mt-0.5">{subValue}</p>}
        </div>
      </CardContent>
    </Card>
  </Link>
);

const CHART_COLORS = ['#F59E0B', '#3B82F6', '#10B981', '#8B5CF6', '#EC4899'];

// Cleaner Dashboard Component
const CleanerDashboard = () => {
  const [appointments, setAppointments] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch both cleanings and installations
        const [cleaningRes, installationRes] = await Promise.all([
          cleanerAPI.getAppointments(),
          scheduledInstallationAPI.getAll().catch(() => ({ data: [] }))
        ]);
        setAppointments(cleaningRes.data);
        
        // Filter installations assigned to this user
        const myInstallations = (installationRes.data || []).filter(i => {
          const assignedTo = i.assigned_to || [];
          return assignedTo.includes(user?.id);
        });
        setInstallations(myInstallations);
      } catch (error) {
        toast.error('Kon data niet laden');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user?.id]);

  const scheduledCleanings = appointments.filter(a => a.status === 'scheduled' || !a.status);
  const completedCleanings = appointments.filter(a => a.status === 'completed');
  
  const scheduledInstallations = installations.filter(i => i.status === 'gepland' || i.status === 'in_uitvoering');
  const completedInstallations = installations.filter(i => i.status === 'voltooid');

  // Sort by date, upcoming first
  const upcomingCleanings = scheduledCleanings.sort((a, b) => {
    const dateA = a.cleaning_date?.split('T')[0] || '';
    const dateB = b.cleaning_date?.split('T')[0] || '';
    return dateA.localeCompare(dateB);
  });
  
  const upcomingInstallations = scheduledInstallations.sort((a, b) => {
    const dateA = a.installation_date || '';
    const dateB = b.installation_date || '';
    return dateA.localeCompare(dateB);
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="h-24 animate-pulse bg-muted" />
          ))}
        </div>
        <Card className="h-64 animate-pulse bg-muted" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cleaner-dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-4xl font-bold tracking-tight">Welkom, {user?.first_name}!</h1>
        <p className="text-muted-foreground mt-1">Hier is een overzicht van je taken</p>
      </div>

      {/* Mijn Tickets */}
      <MyTicketsPanel />

      {/* Stats - Only show Reinigingen and Installaties for Install/Clean */}
      <div className="grid grid-cols-2 gap-3 md:gap-4">
        <Card className="bg-purple-500/10 border-purple-500/30">
          <CardContent className="p-3 md:p-4 flex items-center gap-2 md:gap-3">
            <Sparkles className="w-6 h-6 md:w-8 md:h-8 text-purple-400" />
            <div>
              <p className="text-xl md:text-2xl font-bold">{scheduledCleanings.length}</p>
              <p className="text-xs text-muted-foreground">Reinigingen</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-orange-500/10 border-orange-500/30">
          <CardContent className="p-3 md:p-4 flex items-center gap-2 md:gap-3">
            <Zap className="w-6 h-6 md:w-8 md:h-8 text-orange-400" />
            <div>
              <p className="text-xl md:text-2xl font-bold">{scheduledInstallations.length}</p>
              <p className="text-xs text-muted-foreground">Installaties</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Installations */}
      {upcomingInstallations.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-orange-400" />
                  Komende Installaties
                </CardTitle>
                <CardDescription>Je geplande installaties</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/installaties')}>
                Alle installaties
                <ArrowUpRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {upcomingInstallations.slice(0, 5).map((installation) => {
                const typeLabels = {
                  'zonnepanelen': 'Zonnepanelen',
                  'batterij': 'Batterij',
                  'laadpaal': 'Laadpaal',
                  'omvormer': 'Omvormer',
                  'warmtepomp': 'Warmtepomp',
                  'airco': 'Airco',
                  'combi': 'Combi'
                };
                const typeLabel = typeLabels[installation.installation_type] || 'Installatie';
                
                return (
                  <div 
                    key={installation.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => navigate('/installaties')}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-500/10 rounded-lg">
                        <Zap className="w-5 h-5 text-orange-400" />
                      </div>
                      <div>
                        <p className="font-medium">{installation.customer_name || 'Onbekende klant'}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="w-3 h-3" />
                          {installation.property_street || 'Adres onbekend'}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="bg-orange-500/10 text-orange-400 border-orange-500/30">
                        {typeLabel}
                      </Badge>
                      <p className="text-sm text-muted-foreground mt-1">
                        {formatDate(installation.installation_date)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming Cleanings */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-400" />
                Komende Reinigingen
              </CardTitle>
              <CardDescription>Je geplande reinigingen</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/agenda')}>
              Alle reinigingen
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {upcomingCleanings.length === 0 ? (
            <div className="text-center py-8">
              <Sparkles className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Geen geplande reinigingen</p>
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingCleanings.slice(0, 5).map((cleaning) => {
                const property = cleaning.property;
                let cleaningDate = cleaning.cleaning_date;
                let cleaningTime = '';
                if (cleaningDate && cleaningDate.includes('T')) {
                  const parts = cleaningDate.split('T');
                  cleaningDate = parts[0];
                  cleaningTime = parts[1]?.substring(0, 5) || '';
                }

                return (
                  <div 
                    key={cleaning.id} 
                    className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate('/agenda')}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-md flex items-center justify-center bg-purple-500/20">
                        <Sparkles className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="font-medium">{cleaning.customer_name || 'Onbekend'}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {formatDate(cleaningDate)} {cleaningTime && `om ${cleaningTime}`}
                        </div>
                        {property && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                            <MapPin className="w-3 h-3" />
                            {property.street} {property.number}, {property.city}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {cleaning.panel_count > 0 && (
                        <Badge variant="outline" className="text-xs">
                          {cleaning.panel_count} panelen
                        </Badge>
                      )}
                      <ArrowUpRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                );
              })}
              {upcomingCleanings.length > 5 && (
                <p className="text-center text-sm text-muted-foreground pt-2">
                  + {upcomingCleanings.length - 5} meer reinigingen
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Sales Dashboard Component
const SalesDashboard = () => {
  const [stats, setStats] = useState({
    totalLeads: 0,
    newLeads: 0,
    contactedLeads: 0,
    convertedLeads: 0,
    totalOffertes: 0,
    pendingOffertes: 0,
    acceptedOffertes: 0,
    totalAppointments: 0,
    upcomingAppointments: 0
  });
  const [recentLeads, setRecentLeads] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };

        // Fetch leads, offertes, and appointments
        const [leadsRes, offertesRes, instOffertesRes, appointmentsRes] = await Promise.all([
          fetch(`${API_URL}/api/aanvragen`, { headers }).then(r => r.json()),
          fetch(`${API_URL}/api/offertes`, { headers }).then(r => r.json()),
          fetch(`${API_URL}/api/installation-offertes`, { headers }).then(r => r.json()),
          fetch(`${API_URL}/api/appointments`, { headers }).then(r => r.json())
        ]);

        // Calculate lead stats
        const leads = Array.isArray(leadsRes) ? leadsRes : [];
        const newLeads = leads.filter(l => l.status === 'new').length;
        const contactedLeads = leads.filter(l => l.status === 'contacted').length;
        const convertedLeads = leads.filter(l => l.status === 'converted').length;

        // Calculate offerte stats (combine both types)
        const allOffertes = [
          ...(Array.isArray(offertesRes) ? offertesRes : []),
          ...(Array.isArray(instOffertesRes) ? instOffertesRes : [])
        ];
        const pendingOffertes = allOffertes.filter(o => o.status === 'pending' || o.status === 'sent').length;
        const acceptedOffertes = allOffertes.filter(o => o.status === 'accepted').length;

        // Calculate appointment stats
        const appointments = Array.isArray(appointmentsRes) ? appointmentsRes : [];
        const today = new Date().toISOString().split('T')[0];
        const upcoming = appointments.filter(a => a.date >= today && a.status === 'scheduled');

        setStats({
          totalLeads: leads.length,
          newLeads,
          contactedLeads,
          convertedLeads,
          totalOffertes: allOffertes.length,
          pendingOffertes,
          acceptedOffertes,
          totalAppointments: appointments.length,
          upcomingAppointments: upcoming.length
        });

        setRecentLeads(leads.slice(0, 5));
        setUpcomingAppointments(upcoming.slice(0, 5));
      } catch (error) {
        console.error('Error loading sales dashboard:', error);
        toast.error('Kon dashboard data niet laden');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="h-24 animate-pulse bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  const conversionRate = stats.totalLeads > 0 
    ? Math.round((stats.convertedLeads / stats.totalLeads) * 100) 
    : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sales-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Welkom, {user?.first_name}</h1>
        <p className="text-muted-foreground">Uw sales overzicht</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-blue-500/10 border-blue-500/30 cursor-pointer hover:bg-blue-500/20 transition-colors" onClick={() => navigate('/aanvragen')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Inbox className="w-8 h-8 text-blue-400" />
              <div>
                <p className="text-2xl font-bold">{stats.totalLeads}</p>
                <p className="text-xs text-muted-foreground">Mijn Leads</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-500/10 border-green-500/30 cursor-pointer hover:bg-green-500/20 transition-colors" onClick={() => navigate('/aanvragen?status=new')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-2xl font-bold">{stats.newLeads}</p>
                <p className="text-xs text-muted-foreground">Nieuwe Leads</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-purple-500/10 border-purple-500/30 cursor-pointer hover:bg-purple-500/20 transition-colors" onClick={() => navigate('/offertes')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-purple-400" />
              <div>
                <p className="text-2xl font-bold">{stats.totalOffertes}</p>
                <p className="text-xs text-muted-foreground">Mijn Offertes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-amber-500/10 border-amber-500/30 cursor-pointer hover:bg-amber-500/20 transition-colors" onClick={() => navigate('/agenda')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CalendarIcon className="w-8 h-8 text-amber-400" />
              <div>
                <p className="text-2xl font-bold">{stats.upcomingAppointments}</p>
                <p className="text-xs text-muted-foreground">Komende Afspraken</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Mijn Prestaties</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 rounded-lg bg-muted/50">
              <p className="text-3xl font-bold text-green-400">{conversionRate}%</p>
              <p className="text-sm text-muted-foreground">Conversie Ratio</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/50">
              <p className="text-3xl font-bold text-blue-400">{stats.contactedLeads}</p>
              <p className="text-sm text-muted-foreground">Gecontacteerd</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/50">
              <p className="text-3xl font-bold text-purple-400">{stats.pendingOffertes}</p>
              <p className="text-sm text-muted-foreground">Openstaande Offertes</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/50">
              <p className="text-3xl font-bold text-amber-400">{stats.acceptedOffertes}</p>
              <p className="text-sm text-muted-foreground">Geaccepteerde Offertes</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mijn Tickets */}
      <MyTicketsPanel />

      {/* Recent Leads & Upcoming Appointments */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Recent Leads */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Recente Leads</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/aanvragen')}>
              Bekijk alle
            </Button>
          </CardHeader>
          <CardContent>
            {recentLeads.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Geen leads toegewezen</p>
            ) : (
              <div className="space-y-3">
                {recentLeads.map((lead) => (
                  <div key={lead.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                    <div>
                      <p className="font-medium">{lead.first_name} {lead.last_name}</p>
                      <p className="text-sm text-muted-foreground">{lead.city || 'Onbekend'}</p>
                    </div>
                    <Badge variant={lead.status === 'new' ? 'default' : lead.status === 'contacted' ? 'secondary' : 'outline'}>
                      {lead.status === 'new' ? 'Nieuw' : lead.status === 'contacted' ? 'Gecontacteerd' : lead.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Appointments */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Komende Afspraken</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/agenda')}>
              Bekijk agenda
            </Button>
          </CardHeader>
          <CardContent>
            {upcomingAppointments.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Geen komende afspraken</p>
            ) : (
              <div className="space-y-3">
                {upcomingAppointments.map((apt) => (
                  <div key={apt.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                    <div>
                      <p className="font-medium">{apt.title}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" />
                        {apt.date} {apt.time && `om ${apt.time}`}
                      </p>
                    </div>
                    <Badge variant="outline">
                      {apt.status === 'scheduled' ? 'Gepland' : apt.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [cityData, setCityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const isCleaner = user?.role === 'cleaner';
  const isSales = user?.role === 'sales';

  useEffect(() => {
    // Skip fetching for cleaners and sales
    if (isCleaner || isSales) return;
    
    const fetchData = async () => {
      try {
        const [statsRes, cityRes] = await Promise.all([
          dashboardAPI.getStats(),
          dashboardAPI.getInstallationsByCity()
        ]);
        setStats(statsRes.data);
        setCityData(cityRes.data);
      } catch (error) {
        toast.error('Failed to load dashboard data');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isCleaner, isSales]);

  // If user is a cleaner, show cleaner dashboard
  if (isCleaner) {
    return <CleanerDashboard />;
  }

  // If user is sales, show sales dashboard
  if (isSales) {
    return <SalesDashboard />;
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="h-32 animate-pulse bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Overzicht van je zonne-installaties</p>
      </div>

      {/* Stats Grid - Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Totaal Klanten"
          value={stats?.total_customers || 0}
          color="amber"
          href="/customers"
        />
        <StatCard
          icon={Building2}
          label="Totaal Adressen"
          value={stats?.total_properties || 0}
          color="blue"
          href="/properties"
        />
        <StatCard
          icon={Sun}
          label="Totaal Zonnepanelen"
          value={stats?.total_solar_panels?.toLocaleString() || 0}
          color="amber"
          href="/properties"
        />
        <StatCard
          icon={Inbox}
          label="Aanvragen"
          value={stats?.total_aanvragen || 0}
          color="cyan"
          href="/aanvragen"
        />
      </div>

      {/* Stats Grid - Row 2 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Sparkles}
          label="2-Jaarlijks Contract"
          value={stats?.contracts_2_yearly || 0}
          color="amber"
          href="/properties?contract=2_yearly"
        />
        <StatCard
          icon={Sparkles}
          label="3-Jaarlijks Contract"
          value={stats?.contracts_3_yearly || 0}
          color="blue"
          href="/properties?contract=3_yearly"
        />
        <StatCard
          icon={Sparkles}
          label="Reiniging Nodig"
          value={stats?.needs_cleaning || 0}
          color="purple"
          href="/cleaning"
        />
      </div>

      {/* Mijn Tickets */}
      <MyTicketsPanel />

      {/* Foto's na reiniging te verwerken */}
      {user?.role === 'admin' && <CleaningPhotosPanel />}

      {/* Meldingen */}
      {user?.role === 'admin' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <NotificationsPanel category="reinigingen" />
          <NotificationsPanel category="facturatie" />
          <NotificationsPanel category="overige" />
        </div>
      )}

      {/* Installatiekaart */}
      <Card className="border-border/50 overflow-hidden">
        <CardContent className="p-4 md:p-6">
          <InstallationMap embedded />
        </CardContent>
      </Card>

      {/* Chart Section - Only City Chart */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">Installaties per Stad</CardTitle>
          <CardDescription>Verdeling over locaties</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            {cityData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={cityData}
                    dataKey="count"
                    nameKey="city"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ city, count }) => `${city}: ${count}`}
                    labelLine={false}
                  >
                    {cityData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={CHART_COLORS[index % CHART_COLORS.length]} 
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.3rem'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                Geen stadsdata beschikbaar
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
